import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { getConversationById } from '../getConversationById';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { getConversationMessages } from '../getConversationMessages';
import { Message } from '../../../types/Messaging/Message';
import { useState } from 'react';
import { ConversationDetails } from '../../../types/Messaging/ConversationDetails';

const PAGE_SIZE = 50;
export default function useConversationById({
  isConnected,
  id,
}: {
  id?: number;
  isConnected?: boolean;
}) {
  const { signedAccessToken: authToken } = useSelector(
    (state: RootState) => state.userSession
  );
  const userProfile = useSelector((state: RootState) => state.userProfile);
  const [recipient, setRecipient] =
    useState<ConversationDetails['participants'][0]>();

  const getConDetails = async () => {
    if (!authToken || !id) return;
    try {
      const res = await getConversationById(id, authToken);
      const recipient = (res as ConversationDetails).participants.find(
        ({ role_id }) => userProfile?.role.id !== role_id
      );
      setRecipient(recipient);
      return res as ConversationDetails;
    } catch (error: any) {
      console.error(error);
      throw Error(
        error.status == 500 ? 'Conversation not found' : error.message
      );
    }
  };

  const getConMessages = async ({ pageParam }: any) => {
    if (!authToken || !id) throw new Error('Missing credentials');
    try {
      const res = await getConversationMessages(id, authToken, {
        before: pageParam,
        limit: `${PAGE_SIZE}`,
      });
      return res as Message[];
    } catch (error: any) {
      console.error(error);
      throw Error(error.message);
    }
  };

  const detailQuery = useQuery({
    queryKey: ['conversation', id],
    queryFn: getConDetails,
    enabled:
      Boolean(authToken) && isConnected && Boolean(id) && Boolean(userProfile),
    staleTime: Infinity,
  });

  const messagesQuery = useInfiniteQuery({
    queryKey: ['conversation_messages', id],
    queryFn: getConMessages,
    initialPageParam: null,
    getNextPageParam: (lastPage) => {
      if (!lastPage?.length || lastPage.length < PAGE_SIZE) return undefined;
      return lastPage[lastPage.length - 1].created_at;
    },
    select: (data) => {
      return {
        ...data,
        pages: data?.pages.flat().reverse(),
      };
    },
    enabled: Boolean(authToken) && isConnected && Boolean(id),
  });

  return {
    userRoleId: userProfile?.role?.id,
    detailQuery,
    messagesQuery,
    recipient,
  };
}
